<template>
  <div class="modal portal-modal" :id="id" data-keyboard="false" data-backdrop="static">
    <div
      class="modal-dialog"
      :class="{
        'modal-dialog-scrollable': modalDialogScrollable,
        'modal-xl': modalExtraLarge,
        'modal-lg': modalLarge,
        'modal-dialog-centered': centered,
      }"
    >
      <div class="modal-content data-load" :class="{ 'sk-loading': showSpinner }">
        <sk-spinner v-if="showSpinner"></sk-spinner>
        <div class="modal-header">
          <slot name="title">
            <h4 class="modal-title">{{ modalTitle }}</h4>
          </slot>
          <button type="button" class="close" @click="closeButtonClicked" aria-label="Close">
            <span aria-hidden="true"><i class="fas fa-times close-icon"></i></span>
          </button>
        </div>

        <div class="modal-body">
          <slot name="body"></slot>
        </div>
        <div class="modal-footer" v-if="showPrimaryFooterButton || showSecondaryFooterButton">
          <a
            href="#"
            @click.prevent="secondaryButtonClicked"
            class="secondary-button"
            v-if="showSecondaryFooterButton"
            >{{ secondaryButtonTranslation }}</a
          >

          <vue-button
            :title="primaryButtonTranslation"
            @click.prevent="primaryButtonClicked"
            class="btn-primary"
            :loading="loadingPrimaryFooterButton"
          ></vue-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  CLOSE_BUTTON_CLICKED_MODAL_EVENT,
  PRIMARY_BUTTON_CLICKED_MODAL_EVENT,
  SECONDARY_BUTTON_CLICKED_MODAL_EVENT,
} from "./index";
import { SkSpinner } from "@/components";
import { VueButton } from "@/components/form";

export default {
  name: "VueModal",
  components: {
    SkSpinner,
    VueButton,
  },
  props: {
    modalDialogScrollable: {
      type: Boolean,
      default: false,
    },
    showModal: {
      type: Boolean,
      default: false,
    },
    centered: {
      type: Boolean,
      default: true,
    },
    modalTitle: {
      type: String,
      default: "",
    },
    showSecondaryFooterButton: {
      type: Boolean,
      default: true,
    },
    showPrimaryFooterButton: {
      type: Boolean,
      default: true,
    },
    loadingSecondaryFooterButton: {
      type: Boolean,
      default: false,
    },
    loadingPrimaryFooterButton: {
      type: Boolean,
      default: false,
    },
    primaryFooterButtonTranslation: {
      type: String,
      default: null,
    },
    secondaryFooterButtonTranslation: {
      type: String,
      default: null,
    },
    modalExtraLarge: {
      type: Boolean,
      default: false,
    },
    modalLarge: {
      type: Boolean,
      default: false,
    },
    showSpinner: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // eslint-disable-next-line no-undef
      id: _.uniqueId("modal_"),
    };
  },
  mounted() {
    document.body.appendChild(this.$el);
    // eslint-disable-next-line no-undef
    $(this.$el).on("shown.bs.modal", this.onModalShown);
    // eslint-disable-next-line no-undef
    $(this.$el).on("hidden.bs.modal", this.onModalHidden);
  },
  beforeDestroy() {
    // eslint-disable-next-line no-undef
    $(this.$el).off("shown.bs.modal", this.onModalShown);
    // eslint-disable-next-line no-undef
    $(this.$el).off("hidden.bs.modal", this.onModalHidden);
    this.hide();
    this.removeOrphanBackdrops();
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
  watch: {
    showModal: {
      handler(val) {
        val ? this.show() : this.hide();
      },
      immediate: true,
    },
  },
  computed: {
    secondaryButtonTranslation() {
      return this.$isset(this.secondaryFooterButtonTranslation)
        ? this.secondaryFooterButtonTranslation
        : this.$t("components.modal.close");
    },
    primaryButtonTranslation() {
      return this.$isset(this.primaryFooterButtonTranslation)
        ? this.primaryFooterButtonTranslation
        : this.$t("components.modal.save");
    },
  },
  methods: {
    primaryButtonClicked() {
      this.$emit(PRIMARY_BUTTON_CLICKED_MODAL_EVENT);
    },
    secondaryButtonClicked() {
      this.$emit(SECONDARY_BUTTON_CLICKED_MODAL_EVENT);
    },
    closeButtonClicked() {
      this.$emit(CLOSE_BUTTON_CLICKED_MODAL_EVENT);
    },
    show() {
      this.$nextTick(() => {
        if (this.$el && this.$el.parentNode !== document.body) {
          document.body.appendChild(this.$el);
        }
        // eslint-disable-next-line no-undef
        $(this.$el).modal("show");
      });
    },
    hide() {
      // eslint-disable-next-line no-undef
      $(this.$el).modal("hide");
    },
    onModalShown() {
      this.$emit("shown");
    },
    onModalHidden() {
      this.$emit("hidden");
      this.removeOrphanBackdrops();
    },
    removeOrphanBackdrops() {
      this.$nextTick(() => {
        // eslint-disable-next-line no-undef
        if ($(".modal.show").length === 0) {
          // eslint-disable-next-line no-undef
          $(".modal-backdrop").remove();
          document.body.classList.remove("modal-open");
          document.body.style.removeProperty("padding-right");
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.portal-modal {
  z-index: 1060;

  ::v-deep .modal-content {
    border: 1px solid var(--portal-border);
    border-radius: 18px;
    background: var(--portal-surface);
    color: var(--portal-text);
    box-shadow: var(--portal-shadow);
    overflow: visible;
  }

  ::v-deep .modal-header {
    align-items: center;
    padding: 1.1rem 1.25rem 0.85rem;
    border-bottom: 1px solid var(--portal-border) !important;
  }

  ::v-deep .modal-title {
    margin: 0;
    color: var(--portal-text);
    font-size: 1.1rem;
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  ::v-deep .modal-body {
    padding: 1.15rem 1.25rem 1rem;
    border: 0;

    .form-group {
      margin-bottom: 1rem;
    }

    label,
    .label-description {
      margin-bottom: 0.4rem;
      color: var(--portal-text-muted);
      font-size: 0.78rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    .form-control,
    textarea.form-control,
    input.form-control {
      min-height: 44px;
      border: 1px solid var(--portal-border);
      border-radius: 12px;
      background: var(--portal-input-bg);
      color: var(--portal-text);
      box-shadow: none;

      &:focus {
        border-color: var(--portal-primary);
        box-shadow: 0 0 0 3px var(--portal-ring);
      }
    }

    textarea.form-control {
      min-height: 96px;
      padding-top: 0.7rem;
    }

    .description {
      color: var(--portal-text);
      font-size: 0.95rem;
      font-weight: 600;
      letter-spacing: -0.01em;
      line-height: 1.4;
      word-break: break-word;
    }

    .description a {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      margin: 0.2rem 0.35rem 0.2rem 0;
      padding: 0.35rem 0.65rem;
      border: 1px solid var(--portal-border);
      border-radius: 999px;
      background: var(--portal-surface-muted);
      color: var(--portal-primary);
      font-size: 0.82rem;
      font-weight: 600;
      text-decoration: none;

      &:hover {
        background: var(--portal-primary-soft);
      }
    }

    .card {
      border: 1px solid var(--portal-border);
      border-radius: 14px;
      background: var(--portal-surface);
      box-shadow: var(--portal-shadow-sm);
    }

    .alert {
      border-radius: 12px;
      font-size: 0.88rem;
      font-weight: 600;
    }

    .alert-danger {
      border: 1px solid rgba(220, 38, 38, 0.18);
      background: var(--portal-danger-soft);
      color: var(--portal-danger);
    }
  }

  ::v-deep .modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 0.9rem 1.25rem 1.15rem;
    border-top: 1px solid var(--portal-border) !important;
  }

  ::v-deep .close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    margin: 0;
    padding: 0;
    border-radius: 10px;
    opacity: 1;
    transition: background 0.2s ease;

    &:hover {
      background: var(--portal-surface-muted);
    }

    &:focus {
      outline: 2px solid var(--portal-ring);
      outline-offset: 2px;
    }
  }
}

.secondary-button {
  display: inline-flex;
  align-items: center;
  padding: 0.55rem 0.85rem;
  border-radius: 10px;
  color: var(--portal-text-muted) !important;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  text-decoration: none !important;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: var(--portal-surface-muted);
    color: var(--portal-text) !important;
  }
}

.close-icon {
  color: var(--portal-text-muted);
  font-size: 0.95rem;
}
</style>
